import { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth, useGuestSession } from '@/modules/auth'
import { createPublicApiClient } from '@/shared/api/client'
import { Eye, EyeOff, Users, Sparkles, AlertCircle } from '@/ui/icons'
import { Button } from '@/ui/components/'
import { Input } from '@/ui/components/'
import { Logo } from '@/ui/components'
import { MEDIA_CONFIG } from '@/shared/utils'
import { PageMeta } from '@/components/PageMeta'

export default function SignUp() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const tripId = searchParams.get('trip')
  const guestEmail = searchParams.get('email')
  const betaInviteToken = searchParams.get('invite_token')

  const [formData, setFormData] = useState({
    email: guestEmail || '',
    password: '',
    confirmPassword: '',
    full_name: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [betaInvitation, setBetaInvitation] = useState<any>(null)
  const [loadingInvitation, setLoadingInvitation] = useState(false)
  const confirmationFallback =
    'We sent you a confirmation link. Please verify your email before signing in.'

  const { signup, loginWithGoogle } = useAuth()
  const { clearGuestSession } = useGuestSession()

  useEffect(() => {
    if (betaInviteToken && !guestEmail) {
      setLoadingInvitation(true)
      setError('')
      const loadInvitation = async () => {
        try {
          const publicApi = createPublicApiClient()
          const response = await publicApi.get<{ success: boolean; data: any }>(`/beta-invitations/${betaInviteToken}`)
          setBetaInvitation(response.data)
          if (response.data.email) {
            setFormData(prev => ({ ...prev, email: response.data.email }))
          }
        } catch (err: any) {
          const errorMessage = err?.message || err?.error?.message || err?.error || 'Invalid or expired beta invitation'
          if (err?.status !== 401) {
            setError(errorMessage)
          }
        } finally {
          setLoadingInvitation(false)
        }
      }
      loadInvitation()
    }
  }, [betaInviteToken, guestEmail])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }

    setLoading(true)

    try {
      const userMetadata: Record<string, any> = {
        full_name: formData.full_name
      }

      if (betaInviteToken) {
        userMetadata.beta_invitation_token = betaInviteToken
      }

      const result = await signup({
        email: formData.email,
        password: formData.password,
        user_metadata: userMetadata,
        trip_id: tripId || undefined
      })

      if (result.requiresEmailConfirmation) {
        navigate('/auth/verify', {
          replace: true,
          state: {
            pendingEmail: formData.email,
            confirmationMessage: result.message ?? confirmationFallback
          }
        })
        return
      }

      const wasGuestMerged = (result as any).guestMerged === true

      if (tripId) {
        if (wasGuestMerged) {
          clearGuestSession()
          await new Promise(resolve => setTimeout(resolve, 500))
        }
        navigate(`/trips/${tripId}`)
      } else {
        navigate('/onboarding')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  const contextBadge = betaInviteToken ? (
    loadingInvitation ? (
      <div className="inline-flex items-center gap-1.5 bg-primary/15 text-primary px-3 py-1 rounded-full text-xs font-medium">
        <Sparkles className="w-3.5 h-3.5 animate-pulse" />
        Loading invitation…
      </div>
    ) : betaInvitation ? (
      <div className="inline-flex items-center gap-1.5 bg-primary/15 text-primary px-3 py-1 rounded-full text-xs font-medium">
        <Sparkles className="w-3.5 h-3.5" />
        Beta Invitation
      </div>
    ) : null
  ) : tripId ? (
    <div className="inline-flex items-center gap-1.5 bg-earth-100 text-earth-700 px-3 py-1 rounded-full text-xs font-medium">
      <Users className="w-3.5 h-3.5" />
      You're joining a trip
    </div>
  ) : null

  return (
    <>
      <PageMeta title="Sign up" description="Create your SitRepp.io account to plan overlanding trips with your crew" />
      <div className="min-h-screen flex">
      {/* ── Left panel: immersive photo ── */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative flex-col overflow-hidden">
        <img
          src={MEDIA_CONFIG.mockups.photoSharing}
          alt="Overlanding camp at dusk"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

        {/* Logo */}
        <div className="relative z-10 p-10">
          <Logo to="/" size="sm" className="h-8 brightness-0 invert" />
        </div>

        {/* Brand copy */}
        <div className="relative z-10 mt-auto p-10 pb-14">
          <p className="text-xs font-semibold text-white/60 mb-6">
            Coordinate · Plan · Explore
          </p>
          <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
            Your crew.<br />Your adventure.
          </h1>
          <p className="mt-4 text-base xl:text-lg text-white/75 max-w-sm leading-relaxed">
            Plan routes, manage gear, coordinate meals, and share memories — all from one shared workspace.
          </p>

          {/* Feature chips */}
          <div className="mt-8 flex flex-wrap gap-2">
            {['Route Planning', 'Gear Lists', 'Meal Coordination', 'Photo Sharing'].map(feature => (
              <span
                key={feature}
                className="px-3 py-1 rounded-full text-xs font-medium bg-white/10 text-white/80 border border-white/15"
              >
                {feature}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right panel: form ── */}
      <div
        className="w-full lg:w-1/2 xl:w-2/5 relative flex flex-col min-h-screen"
        style={{
          backgroundImage: `url(${MEDIA_CONFIG.mockups.photoSharing})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Mobile overlay */}
        <div className="absolute inset-0 bg-black/65 backdrop-blur-sm lg:hidden" />
        {/* Desktop solid background */}
        <div className="absolute inset-0 bg-background hidden lg:block" />

        <div className="relative z-10 flex flex-col min-h-screen px-8 py-10 sm:px-12">
          {/* Mobile logo */}
          <div className="flex justify-center mb-6 lg:hidden">
            <Logo to="/" size="sm" className="h-10 brightness-0 invert" />
          </div>

          {/* Scrollable form area */}
          <div className="flex-1 flex flex-col justify-center w-full max-w-sm mx-auto">
            {/* Heading */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white lg:text-foreground">
                Create your account
              </h2>
              {contextBadge ? (
                <div className="mt-2">{contextBadge}</div>
              ) : (
                <p className="mt-1.5 text-sm text-white/70 lg:text-muted-foreground">
                  {betaInvitation
                    ? "You've been invited to join SitRepp.io Beta!"
                    : tripId
                    ? 'Sign up to join this adventure'
                    : 'Start planning your next adventure'}
                </p>
              )}
            </div>

            {/* Google SSO */}
            <>
              <button
                type="button"
                onClick={loginWithGoogle}
                className="w-full flex items-center justify-center gap-3 rounded-xl border px-4 py-3 text-sm font-medium shadow-sm transition-colors border-white/30 bg-white/10 text-white hover:bg-white/20 lg:border-input lg:bg-background lg:text-foreground lg:hover:bg-muted"
              >
                <svg className="h-4 w-4 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </button>

              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/20 lg:border-border" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 text-white/50 lg:text-muted-foreground lg:bg-background">
                    Or continue with email
                  </span>
                </div>
              </div>
            </>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-1.5">
                <label htmlFor="full_name" className="text-sm font-medium text-white lg:text-foreground">
                  Full Name
                </label>
                <Input
                  id="full_name"
                  name="full_name"
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder="Jane Overland"
                  className="h-11 rounded-xl"
                />
              </div>

              <div className="space-y-1.5">
                <label htmlFor="email" className="text-sm font-medium text-white lg:text-foreground">
                  Email address
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  readOnly={!!guestEmail}
                  disabled={!!guestEmail}
                  className={`h-11 rounded-xl ${guestEmail ? 'bg-muted cursor-not-allowed' : ''}`}
                />
                {guestEmail && (
                  <p className="text-xs text-white/60 lg:text-muted-foreground">
                    Email pre-filled from your guest session
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <label htmlFor="password" className="text-sm font-medium text-white lg:text-foreground">
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="h-11 rounded-xl pr-10"
                    placeholder="Min. 6 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <label htmlFor="confirmPassword" className="text-sm font-medium text-white lg:text-foreground">
                  Confirm Password
                </label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="h-11 rounded-xl pr-10"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 rounded-xl font-semibold mt-2"
                disabled={loading}
              >
                {loading ? 'Creating account…' : 'Create account'}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-white/60 lg:text-muted-foreground">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-semibold text-white hover:text-white/80 lg:text-primary lg:hover:text-primary/80 transition-colors"
              >
                Sign in →
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}
